import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/contexts/ProductsContext';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';
import { useWishlist } from '@/hooks/useWishlist';
import { textOf, useShopConfig, useShopMembers, type ShopEntry, type ShopKind } from '@/lib/shopConfig';
import { thumbOf } from '@/lib/thumb';
import { useSiteContentValue } from '@/hooks/useSiteContentValue';
import { usePageSections } from '@/hooks/usePageSections';
import { trackAddToCart } from '@/lib/tracking';
import { IMAGES } from '@/constants/images';
import { YtBg, YtPlayer, ytId, ytThumb } from '@/shared/rxk-product/media';
import './home-v2.css';

// New-design Home (shown when useSiteDesign().isNew). Layout and styles come
// from the approved HTML draft; products, prices and the 3-Step Set come
// from ProductsContext, so new products and skin types appear without code
// changes. Every text, image, banner link and section switch is editable in
// Admin > Website > Home page > New design (page key 'homeV2').

const SET_PATH = '/3-step-treatment';
const A = '/v2/';

const CATEGORY_OF_STEP: Record<string, { key: string; label: string }> = {
  OPEN: { key: 'essence', label: 'homeV2.shop.catEssence' },
  TREAT: { key: 'serum', label: 'homeV2.shop.catSerum' },
  LOCK: { key: 'cream', label: 'homeV2.shop.catCream' },
};

const toNumber = (price: string) => Number(price.replace(/[^0-9.]/g, '')) || 0;

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
function Play() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 5l12 7-12 7z" />
    </svg>
  );
}
const ICON: Record<string, ReactNode> = {
  cod: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  ),
  van: (
    <svg viewBox="0 0 24 24">
      <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" />
      <circle cx="7" cy="17" r="1.6" />
      <circle cx="17" cy="17" r="1.6" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  flask: (
    <svg viewBox="0 0 24 24">
      <path d="M9 3h6v5l4 10a2 2 0 0 1-2 3H7a2 2 0 0 1-2-3l4-10z" />
      <path d="M8 14h8" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
    </svg>
  ),
  cal: (
    <svg viewBox="0 0 24 24">
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3v4M15 3v4" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24">
      <path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  ),
  bag: (
    <svg viewBox="0 0 24 24">
      <path d="M5 8h14l-1 12H6z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </svg>
  ),
};

interface ShopItem {
  key: string;
  slug: string;
  name: string;
  href: string;
  price: number;
  priceText: string;
  oldText?: string;
  sale: boolean;
  img: string;
  img2: string;
  order: number;
}

// A shop circle photo: loads the small copy (thumbOf), falls back to the
// original if that fails, and fades in only once it has fully loaded.
function CircleImg({ src, face }: { src: string; face?: boolean }) {
  const [fallback, setFallback] = useState(false);
  const [ok, setOk] = useState(false);
  if (!src) return null;
  const url = fallback ? src : thumbOf(src);
  return (
    <img
      className={[face ? 'face' : '', ok ? 'ok' : ''].filter(Boolean).join(' ') || undefined}
      src={url}
      alt=""
      decoding="async"
      onLoad={() => setOk(true)}
      onError={() => (url !== src ? setFallback(true) : setOk(true))}
    />
  );
}

// Adds "rv" elements' "in" class once they scroll into view. Re-scans after
// every render so sections that appear later are picked up too.
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const io = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    io.current = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.current?.unobserve(e.target);
          }
        }),
      { threshold: 0.12 },
    );
    const el = ref.current;
    return () => {
      io.current?.disconnect();
      el?.querySelectorAll('[data-io]').forEach((n) => n.removeAttribute('data-io'));
    };
  }, []);
  useEffect(() => {
    ref.current?.querySelectorAll('.rv:not(.in):not([data-io])').forEach((el) => {
      el.setAttribute('data-io', '');
      io.current?.observe(el);
    });
  });
  return ref;
}

// Plays a muted clip only while it is on screen.
function InViewVideo(props: React.VideoHTMLAttributes<HTMLVideoElement>) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause()), { threshold: 0.35 });
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return <video ref={ref} muted loop playsInline preload="none" {...props} />;
}

// `only` limits the page to the given sections (the Products page shows just the shop).
export default function HomeV2({ only }: { only?: string[] } = {}) {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { products, routineSet } = useProducts();
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();
  const root = useReveal();
  const showSection = usePageSections('homeV2');
  const show = (key: string) => (!only || only.includes(key)) && showSection(key);

  // ── Banners ──
  // Up to four banners; an empty image hides that slot. Links are site paths
  // like /3-step-treatment (the language prefix is added here) or full URLs.
  const b1 = useSiteImage('home.v2.banner1', A + 'banner-1-d.jpg');
  const b2 = useSiteImage('home.v2.banner2', A + 'banner-2-d.jpg');
  const b3 = useSiteImage('home.v2.banner3', A + 'banner-3-d.jpg');
  const b4 = useSiteImage('home.v2.banner4', '');
  const l1 = useSiteContentValue('homeV2.hero.banner1Link', 'en', SET_PATH);
  const l2 = useSiteContentValue('homeV2.hero.banner2Link', 'en', SET_PATH);
  const l3 = useSiteContentValue('homeV2.hero.banner3Link', 'en', '/products/skin-priming-booster-essence');
  const l4 = useSiteContentValue('homeV2.hero.banner4Link', 'en', '/products');
  const banners = [
    { src: b1, href: l1, dark: false },
    { src: b2, href: l2, dark: true },
    { src: b3, href: l3, dark: false },
    { src: b4, href: l4, dark: false },
  ]
    .filter((b) => b.src)
    .map((b) => ({
      ...b,
      external: /^https?:/.test(b.href),
      href: /^https?:/.test(b.href) ? b.href : prefix + b.href,
    }));
  const tile1Img = useSiteImage('home.v2.tile1', A + 'giftbox.webp');
  const tile2Img = useSiteImage('home.v2.tile2', A + 'booster-pack.webp');
  const brandImg1 = useSiteImage('home.v2.brand1', A + 'seoul-gate.jpg');
  const brandImg2 = useSiteImage('home.v2.brand2', A + 'booster-o0027p.webp');
  const holoImg = useSiteImage('home.v2.hologram', A + 'hologram-sticker.jpg');
  const g1 = useSiteImage('home.launchGallery.crowd', IMAGES.crowd);
  // Watch & shop: videos and covers from Admin › Home Page › Watch & shop
  // (MP4 upload or link); empty = the built-in clips.
  const watchFull = useSiteContentValue('homeV2.watch.video.full', 'en', '');
  const watchFullCover = useSiteImage('home.v2.watch.full', A + 'vid-routine.jpg');
  const watchVid: Record<string, string> = {
    booster: useSiteContentValue('homeV2.watch.video.booster', 'en', ''),
    serum: useSiteContentValue('homeV2.watch.video.serum', 'en', ''),
    cream: useSiteContentValue('homeV2.watch.video.cream', 'en', ''),
  };
  const watchCover: Record<string, string> = {
    booster: useSiteImage('home.v2.watch.booster', A + 'vid-booster.jpg'),
    serum: useSiteImage('home.v2.watch.serum', A + 'vid-serum.jpg'),
    cream: useSiteImage('home.v2.watch.cream', A + 'vid-cream.jpg'),
  };
  const fullSrc = watchFull || A + 'vid-routine.mp4';
  const g2 = useSiteImage('home.launchGallery.model', IMAGES.model);
  const g3 = useSiteImage('home.launchGallery.consult', IMAGES.consult);
  const g4 = useSiteImage('home.launchGallery.booth', IMAGES.booth);
  const g5 = useSiteImage('home.launchGallery.brandPanel', IMAGES.brandPanel);
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);
  useEffect(() => {
    if (banners.length < 2 || paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setTimeout(() => setSlide((s) => (s + 1) % banners.length), 6000);
    return () => window.clearTimeout(id);
  }, [slide, paused, banners.length]);
  const go = (i: number) => setSlide((i + banners.length) % banners.length);

  // ── Shop data ──
  const items: ShopItem[] = useMemo(() => {
    const list: ShopItem[] = [];
    if (routineSet) {
      list.push({
        key: 'set',
        slug: '3-step-set',
        name: routineSet.designV2?.name || routineSet.name,
        href: prefix + SET_PATH,
        price: toNumber(routineSet.price),
        priceText: routineSet.price,
        oldText: routineSet.originalPrice || undefined,
        sale: !!routineSet.originalPrice && toNumber(routineSet.originalPrice) > toNumber(routineSet.price),
        img: routineSet.image,
        img2: A + 'giftbox.webp',
        order: 0,
      });
    }
    products.forEach((p, i) =>
      list.push({
        key: p.id,
        slug: p.id,
        name: p.designV2?.name || p.name,
        href: `${prefix}/products/${p.id}`,
        price: toNumber(p.price),
        priceText: p.price,
        sale: false,
        img: p.image,
        img2: p.galleryImages[0] || p.image,
        order: i + 1,
      }),
    );
    return list;
  }, [products, routineSet, prefix]);

  // Category and concern circles from Admin > Website > Home Page > Shop categories & concerns.
  // Each product picks its circles under Admin > Products (shopKeysOf has the fallback).
  const shop = useShopConfig();
  const members = useShopMembers();
  // Until the products have loaded, every visible circle is shown (with its own
  // photo) so the row keeps its shape instead of growing from "All" alone.
  const loadingItems = items.length === 0;
  const entries = useCallback(
    (list: ShopEntry[], kind: ShopKind) =>
      list
        .filter((e) => !e.hidden)
        .map((e) => {
          const slugs = items.filter((i) => members.find((m) => m.slug === i.slug)?.[kind].includes(e.key)).map((i) => i.slug);
          return {
            key: e.key,
            label: textOf(e.label, currentLang),
            long: textOf(e.long, currentLang) || textOf(e.label, currentLang),
            slugs,
            img: e.image || items.find((i) => i.slug === slugs[0])?.img || '',
          };
        })
        .filter((e) => loadingItems || e.slugs.length > 0),
    [items, members, currentLang, loadingItems],
  );
  const cats = useMemo(
    () => [
      { key: 'all', label: t('homeV2.shop.all'), long: '', slugs: items.map((i) => i.slug), img: shop.allImage || routineSet?.image || items[0]?.img || '' },
      ...entries(shop.categories, 'categories'),
    ],
    [entries, shop, items, routineSet, t],
  );
  const concerns = useMemo(() => entries(shop.concerns, 'concerns'), [entries, shop]);
  const wishlist = useWishlist();
  const toggleWish = (it: ShopItem) => showToast(wishlist.toggle(it.slug) ? 'Saved to your wishlist' : 'Removed from your wishlist');

  const [cat, setCat] = useState('all');
  const [skin, setSkin] = useState('');
  const [sort, setSort] = useState<'featured' | 'new' | 'low' | 'high'>('featured');
  const [sortOpen, setSortOpen] = useState(false);
  const [shown, setShown] = useState(12);
  const [catClosed, setCatClosed] = useState(false);
  // Header and drawer links land here as /?cat=<key> or /?concern=<key>.
  const location = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const c = q.get('cat');
    const k = q.get('concern');
    if (!c && !k) return;
    setCat(c || 'all');
    setSkin(k || '');
    setShown(12);
    window.setTimeout(() => document.getElementById('shop')?.scrollIntoView({ block: 'start' }), 50);
  }, [location.search]);
  // The set's purchase bar slides up once the hero has scrolled away.
  const heroRef = useRef<HTMLElement>(null);
  const [sbar, setSbar] = useState(false);
  useEffect(() => {
    const on = () => setSbar(!!heroRef.current && heroRef.current.getBoundingClientRect().bottom < 0);
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  const sortRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => sortRef.current && !sortRef.current.contains(e.target as Node) && setSortOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const SORTS = {
    featured: t('homeV2.shop.sortFeatured'),
    new: t('homeV2.shop.sortNew'),
    low: t('homeV2.shop.sortLow'),
    high: t('homeV2.shop.sortHigh'),
  };
  const rows = items
    .filter((i) => (cat === 'all' || !!cats.find((c) => c.key === cat)?.slugs.includes(i.slug)) && (!skin || !!concerns.find((c) => c.key === skin)?.slugs.includes(i.slug)))
    .sort((a, b) => (sort === 'low' ? a.price - b.price : sort === 'high' ? b.price - a.price : a.order - b.order));

  const add = (it: ShopItem) => {
    addToCart(it.slug, 1);
    trackAddToCart({
      id: it.slug,
      name: it.name,
      price: it.price,
      quantity: 1,
    });
    showToast(t('cart.added', 'Added to Cart ✓'), t('cart.viewCart', 'View Cart →'), openCart);
  };

  // ── Video dialogs ──
  const vlb = useRef<HTMLDialogElement>(null);
  const vlbVideo = useRef<HTMLVideoElement>(null);
  const [vlbBuy, setVlbBuy] = useState<{ href: string; label: string }>({
    href: prefix + SET_PATH,
    label: 'Shop now',
  });
  // Opens the video lightbox: a step's own video from the start, or the
  // full routine video at that step's moment.
  const [vlbSrc, setVlbSrc] = useState('');
  const [vlbStart, setVlbStart] = useState(0);
  const [vlbOpen, setVlbOpen] = useState(false);
  const openRoutine = (start: number, href: string, label: string, src?: string) => {
    setVlbBuy({ href, label });
    setVlbSrc(src || '');
    setVlbStart(start);
    setVlbOpen(true);
    vlb.current?.showModal();
    // A YouTube video plays in its own player (rendered while the dialog is open).
    if (ytId(src || fullSrc)) return;
    const v = vlbVideo.current;
    if (!v) return;
    const play = () => {
      v.currentTime = start;
      v.play().catch(() => {});
    };
    if (v.readyState > 0) play();
    else {
      v.addEventListener('loadedmetadata', play, { once: true });
      v.load();
    }
  };
  const guide = useRef<HTMLDialogElement>(null);
  const guideVideo = useRef<HTMLVideoElement>(null);
  const closeDialog = (d: React.RefObject<HTMLDialogElement | null>, v: React.RefObject<HTMLVideoElement | null>) => {
    v.current?.pause();
    d.current?.close();
  };

  // ── Gallery lightbox ──
  const launch = [g1, g2, g3, g4, g5].map((src, i) => ({
    src,
    cap: t(`homeV2.launch.cap${i + 1}`),
  }));
  const lbx = useRef<HTMLDialogElement>(null);
  const [lbIndex, setLbIndex] = useState(0);

  // ── Hologram tilt ──
  const hcard = useRef<HTMLSpanElement>(null);
  const tilt = (e: React.PointerEvent) => {
    const h = hcard.current;
    if (!h) return;
    const r = h.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    h.style.transform = `rotate(-6deg) rotateY(${x * 24}deg) rotateX(${-y * 24}deg) scale(1.04)`;
    (h.lastElementChild as HTMLElement).style.backgroundPosition = `${50 - x * 120}% 0`;
  };
  const untilt = () => {
    const h = hcard.current;
    if (!h) return;
    h.style.transform = '';
    (h.lastElementChild as HTMLElement).style.backgroundPosition = '';
  };

  const steps = products.filter((p) => CATEGORY_OF_STEP[p.stepKeyword]);
  const stepTime: Record<string, string> = {
    OPEN: '30 sec',
    TREAT: '60 sec',
    LOCK: '3 min',
  };
  const clips: Record<string, { file: string; start: number }> = {
    OPEN: { file: 'booster', start: 9.6 },
    TREAT: { file: 'serum', start: 18.2 },
    LOCK: { file: 'cream', start: 27.4 },
  };

  return (
    <div className="v2" ref={root}>
      {/* Hero: banners with two promo tiles */}
      {show('hero') && (
        <section className="kv" aria-label="Featured" ref={heroRef}>
          <div className="kvgrid">
            <div
              className={`kvin${paused ? ' paused' : ''}${banners[slide]?.dark ? ' dark' : ''}`}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                if (Math.abs(dx) > 40) go(slide + (dx < 0 ? 1 : -1));
                touchX.current = null;
              }}
              aria-roledescription="carousel"
            >
              {banners.map((b, i) =>
                b.external ? (
                  <a key={i} href={b.href} className={`kvs${i === slide ? ' on' : ''}`} aria-label={`Banner ${i + 1} of ${banners.length}`}>
                    <img src={b.src} alt="" loading={i === 0 ? 'eager' : 'lazy'} />
                  </a>
                ) : (
                  <Link key={i} to={b.href} className={`kvs${i === slide ? ' on' : ''}`} aria-label={`Banner ${i + 1} of ${banners.length}`}>
                    <img src={b.src} alt="" loading={i === 0 ? 'eager' : 'lazy'} />
                  </Link>
                ),
              )}
              {banners.length > 1 && (
                <>
                  <button type="button" className="kva prev" aria-label="Previous banner" onClick={() => go(slide - 1)}>
                    <Arrow />
                  </button>
                  <button type="button" className="kva next" aria-label="Next banner" onClick={() => go(slide + 1)}>
                    <Arrow />
                  </button>
                  <div className="kvd" style={{ ['--t' as string]: '6000ms' }}>
                    {banners.map((_, i) => (
                      <button key={`${i}-${i === slide ? slide : ''}`} type="button" aria-label={`Banner ${i + 1}`} className={i === slide ? 'on' : ''} onClick={() => go(i)}>
                        <i />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="kvside">
              <Link className="kt" to={prefix + SET_PATH}>
                <img src={tile1Img} alt="" loading="lazy" />
                <span>
                  <small>{t('homeV2.hero.tile1Label')}</small>
                  <b>
                    {routineSet?.savings
                      ? t('homeV2.hero.tile1Title', {
                          amount: routineSet.savings,
                        })
                      : t('homeV2.ritual.cta')}
                  </b>
                </span>
                <i className="ka" aria-hidden="true">
                  <Arrow />
                </i>
              </Link>
              <a className="kt m" href="#verify">
                <img src={tile2Img} alt="" loading="lazy" />
                <span>
                  <small>{t('homeV2.hero.tile2Label')}</small>
                  <b>{t('homeV2.hero.tile2Title')}</b>
                </span>
                <i className="ka" aria-hidden="true">
                  <Arrow />
                </i>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Shop */}
      {show('shop') && (
        <section className="sec" id="shop">
          <div className="wrap">
            <div className="qcat rv" aria-label="Shop by category and concern">
              <div className="qg">
                <span className="qh">{t('homeV2.shop.byCategory')}</span>
                <div className="qr">
                  {cats.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      aria-pressed={!skin && cat === c.key}
                      onClick={() => {
                        setCat(c.key);
                        setSkin('');
                        setShown(12);
                      }}
                    >
                      <span className="qi">
                        <span className="qm">
                          <CircleImg key={c.img} src={c.img} />
                        </span>
                      </span>
                      <span className="ql">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {concerns.length > 0 && (
                <>
                  <span className="qsep" aria-hidden="true" />
                  <div className="qg">
                    <span className="qh">{t('homeV2.shop.byConcern')}</span>
                    <div className="qr">
                      {concerns.map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          aria-pressed={skin === c.key}
                          onClick={() => {
                            setSkin(c.key);
                            setCat('all');
                            setShown(12);
                          }}
                        >
                          <span className="qi">
                            <span className="qm">
                              <CircleImg key={c.img} src={c.img} face />
                            </span>
                          </span>
                          <span className="ql">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="shopwrap">
              <aside className="side" aria-label="Filters">
                <div className={`sg${catClosed ? ' closed' : ''}`}>
                  <h4>
                    <button type="button" className="sgt" aria-expanded={!catClosed} onClick={() => setCatClosed((c) => !c)}>
                      {t('homeV2.shop.categoryHeading')}
                    </button>
                  </h4>
                  <ul>
                    {cats.map((c) => (
                      <li key={c.key}>
                        <button type="button" aria-pressed={cat === c.key} onClick={() => setCat(c.key)}>
                          {c.key === 'all' ? t('homeV2.shop.allProducts') : c.label}
                          <span>{c.slugs.length}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
              <div>
                <div className="tbar">
                  <p style={{ margin: 0 }}>
                    <b>{rows.length}</b> {rows.length === 1 ? t('homeV2.shop.product') : t('homeV2.shop.products')}
                    {skin && (
                      <span className="fchip">
                        {concerns.find((c) => c.key === skin)?.long}
                        <button type="button" aria-label="Clear filter" onClick={() => setSkin('')}>
                          ×
                        </button>
                      </span>
                    )}
                  </p>
                  <div className={`sortbox${sortOpen ? ' open' : ''}`} ref={sortRef}>
                    <button type="button" className="sortbtn" aria-haspopup="listbox" aria-expanded={sortOpen} onClick={() => setSortOpen((o) => !o)}>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 7h11M4 12h7M4 17h4M17 5v14M14 16l3 3 3-3" />
                      </svg>
                      <span>{t('homeV2.shop.sortLabel')}</span>
                      <b>{SORTS[sort]}</b>
                      <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M7 10l5 5 5-5" />
                      </svg>
                    </button>
                    {sortOpen && (
                      <ul className="sortmenu" role="listbox">
                        {(['featured', 'new', 'low', 'high'] as const).map((k) => (
                          <li
                            key={k}
                            role="option"
                            aria-selected={sort === k}
                            tabIndex={0}
                            onClick={() => {
                              setSort(k);
                              setSortOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setSort(k);
                                setSortOpen(false);
                              }
                            }}
                          >
                            {SORTS[k]}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="pgrid" aria-live="polite">
                  {rows.slice(0, shown).map((it, i) => (
                    <article key={it.key} className="pc2" style={{ ['--d' as string]: `${(i % 12) * 0.05}s` }}>
                      <div className="pim">
                        <Link to={it.href} aria-label={it.name}>
                          <img src={it.img} alt={it.name} loading="lazy" />
                          <img src={it.img2} alt="" loading="lazy" />
                        </Link>
                        {it.sale && <span className="sale">{t('homeV2.shop.sale')}</span>}
                        <div className="acts">
                          <button type="button" className="ia wish" aria-pressed={wishlist.has(it.slug)} aria-label={`Save ${it.name}`} onClick={() => toggleWish(it)}>
                            <svg viewBox="0 0 24 24">
                              <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
                            </svg>
                          </button>
                          <button type="button" className="ia" aria-label={`Add ${it.name} to cart`} onClick={() => add(it)}>
                            {ICON.bag}
                          </button>
                        </div>
                      </div>
                      <h3>
                        <Link to={it.href}>{it.name}</Link>
                      </h3>
                      <div className={`pprice${it.sale ? ' on' : ''}`}>
                        <b>{it.priceText}</b>
                        {it.sale && it.oldText && <s>{it.oldText}</s>}
                      </div>
                    </article>
                  ))}
                </div>
                {rows.length === 0 && (
                  <p className="empty">
                    {t('homeV2.shop.empty')}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setCat('all');
                        setSkin('');
                      }}
                    >
                      {t('homeV2.shop.showAll')}
                    </button>
                  </p>
                )}
                <div className="more3">
                  {rows.length > shown && (
                    <button type="button" className="btn ghost" onClick={() => setShown((s) => s + 12)}>
                      {t('homeV2.shop.showMore')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* The daily ritual */}
      {show('ritual') && steps.length > 0 && (
        <section className="sec soft">
          <div className="wrap rout">
            <figure className="rv">
              <img src={routineSet?.image || steps[0].image} alt="Booster, Serum and Cream" loading="lazy" />
            </figure>
            <div className="rv" style={{ ['--d' as string]: '.1s' }}>
              <div className="sh">
                <span className="lbl">{t('homeV2.ritual.label')}</span>
                <h2>
                  {t('homeV2.ritual.heading')}
                  <br />
                  <em>{t('homeV2.ritual.headingEm')}</em>
                </h2>
              </div>
              <div className="rrows">
                {steps.map((p, i) => (
                  <Link key={p.id} to={`${prefix}/products/${p.id}`}>
                    <span className="no">{String(i + 1).padStart(2, '0')}</span>
                    <img src={p.image} alt="" loading="lazy" />
                    <span>
                      <small>
                        STEP {String(i + 1).padStart(2, '0')} · {p.stepKeyword}
                      </small>
                      <b>{p.designV2?.name || p.name}</b>
                      <p>{p.designV2?.homeLine || p.subtitle}</p>
                    </span>
                    <span className="tm">{stepTime[p.stepKeyword] ?? ''}</span>
                  </Link>
                ))}
              </div>
              {routineSet && (
                <Link className="pbtn" to={prefix + SET_PATH}>
                  <span>
                    {t('homeV2.ritual.cta')} · {routineSet.price}
                  </span>
                  <i>
                    <Arrow />
                  </i>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Watch & shop */}
      {show('watch') && (
        <section className="sec">
          <div className="wrap">
            <div className="sh rv">
              <span className="lbl">{t('homeV2.watch.label')}</span>
              <h2>
                {t('homeV2.watch.heading')} <em>{t('homeV2.watch.headingEm')}</em>
              </h2>
            </div>
            <div className="vrow rv">
              {routineSet && (
                <button type="button" className="vc" onClick={() => openRoutine(0, prefix + SET_PATH, `${t('homeV2.ritual.cta')} · ${routineSet.price}`)}>
                  {ytId(watchFull) && <YtBg url={watchFull} poster={watchFullCover === A + 'vid-routine.jpg' ? ytThumb(watchFull) : watchFullCover} className="vbg" />}
                  {!ytId(watchFull) && <img src={watchFullCover} alt="" />}
                  <span className="vt">{t('homeV2.watch.full')}</span>
                  {!watchFull && <span className="vd">0:40</span>}
                  <span className="vp">
                    <Play />
                  </span>
                  <span className="vi">
                    <img src={routineSet.image} alt="" />
                    <span>
                      <b>{routineSet.designV2?.name || routineSet.name}</b>
                      <em>{routineSet.price}</em>
                    </span>
                  </span>
                </button>
              )}
              {steps
                .filter((p) => clips[p.stepKeyword])
                .map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    className="vc"
                    onClick={() =>
                      openRoutine(
                        watchVid[clips[p.stepKeyword].file] ? 0 : clips[p.stepKeyword].start,
                        `${prefix}/products/${p.id}`,
                        `${t('homeV2.watch.shopNow')} · ${p.price}`,
                        watchVid[clips[p.stepKeyword].file],
                      )
                    }
                  >
                    {ytId(watchVid[clips[p.stepKeyword].file]) ? (
                      <YtBg url={watchVid[clips[p.stepKeyword].file]} poster={watchCover[clips[p.stepKeyword].file]} className="vbg" />
                    ) : (
                      <InViewVideo src={watchVid[clips[p.stepKeyword].file] || `${A}vid-${clips[p.stepKeyword].file}.mp4`} poster={watchCover[clips[p.stepKeyword].file]} />
                    )}
                    <span className="vt">
                      {t('homeV2.watch.step')} {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="vi">
                      <img src={p.image} alt="" />
                      <span>
                        <b>{p.designV2?.name || p.name}</b>
                        <em>{p.price}</em>
                      </span>
                    </span>
                  </button>
                ))}
            </div>
          </div>
        </section>
      )}
      <dialog
        className="vlb"
        ref={vlb}
        aria-label="Video"
        onClick={(e) => e.target === vlb.current && closeDialog(vlb, vlbVideo)}
        onClose={() => {
          setVlbOpen(false);
          vlbVideo.current?.pause();
        }}
      >
        {ytId(vlbSrc || fullSrc) ? (
          vlbOpen && <YtPlayer url={vlbSrc || fullSrc} start={vlbStart} />
        ) : (
          <video ref={vlbVideo} src={vlbSrc || fullSrc} poster={vlbSrc ? undefined : watchFullCover} controls playsInline preload="none" />
        )}
        <div className="vbuy">
          <Link to={vlbBuy.href} onClick={() => closeDialog(vlb, vlbVideo)}>
            {vlbBuy.label} <Arrow />
          </Link>
        </div>
        <button type="button" className="vx" aria-label="Close video" onClick={() => closeDialog(vlb, vlbVideo)}>
          ×
        </button>
      </dialog>

      {/* Brand story */}
      {show('brand') && (
        <section className="sec">
          <div className="wrap brand">
            <div className="bcol rv">
              <figure className="b1">
                <img src={brandImg1} alt="Gwanghwamun Gate, Seoul" loading="lazy" />
              </figure>
              <figure className="b2">
                <img src={brandImg2} alt="Skin Priming Booster Essence" loading="lazy" />
              </figure>
              <span className="tag2">Seoul → Dhaka</span>
            </div>
            <div className="btx rv" style={{ ['--d' as string]: '.1s' }}>
              <div className="bhead">
                <span className="bk">
                  {t('homeV2.brand.kicker')} <i lang="ko">서울 → 다카</i>
                </span>
                <h2>
                  {t('homeV2.brand.heading')}
                  <br />
                  <em>{t('homeV2.brand.headingEm')}</em>
                </h2>
              </div>
              <p className="lead">
                <b>Rx.K Seoul</b> {t('homeV2.brand.body')}
              </p>
              <div className="facts2">
                {(['globe', 'flask', 'shield', 'cal'] as const).map((ic, i) => (
                  <div key={ic}>
                    <i>{ICON[ic]}</i>
                    <b>{t(`homeV2.brand.fact${i + 1}Title`)}</b>
                    <span>{t(`homeV2.brand.fact${i + 1}Body`)}</span>
                  </div>
                ))}
              </div>
              <div className="bcta">
                <Link className="pbtn" to={prefix + '/brand-story'}>
                  <span>{t('homeV2.brand.ctaStory')}</span>
                  <i>
                    <Arrow />
                  </i>
                </Link>
                <Link className="sbtn" to={prefix + '/where-to-buy'}>
                  {ICON.pin}
                  <span>{t('homeV2.brand.ctaWhere')}</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Launch gallery */}
      {show('launch') && (
        <section className="sec">
          <div className="wrap">
            <div className="sh rv">
              <span className="lbl">{t('homeV2.launch.label')}</span>
              <h2>
                {t('homeV2.launch.heading')} <em>{t('homeV2.launch.headingEm')}</em>
              </h2>
              <p>{t('homeV2.launch.subtitle')}</p>
            </div>
            <div className="launch rv">
              {launch.map((l, i) => (
                <figure
                  key={l.src}
                  tabIndex={0}
                  onClick={() => {
                    setLbIndex(i);
                    lbx.current?.showModal();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setLbIndex(i);
                      lbx.current?.showModal();
                    }
                  }}
                >
                  <img src={l.src} alt={l.cap} loading="lazy" />
                  <figcaption>
                    <small>{t('homeV2.launch.label')}</small>
                    {l.cap}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}
      <dialog
        className="lbx"
        ref={lbx}
        aria-label="Photo"
        onClick={(e) => e.target === lbx.current && lbx.current?.close()}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setLbIndex((i) => (i - 1 + launch.length) % launch.length);
          if (e.key === 'ArrowRight') setLbIndex((i) => (i + 1) % launch.length);
        }}
      >
        <img src={launch[lbIndex].src} alt={launch[lbIndex].cap} />
        <p>{launch[lbIndex].cap}</p>
        <button type="button" className="lx" aria-label="Close" onClick={() => lbx.current?.close()}>
          ×
        </button>
        <button type="button" className="lp" aria-label="Previous photo" onClick={() => setLbIndex((i) => (i - 1 + launch.length) % launch.length)}>
          <Arrow />
        </button>
        <button type="button" className="ln" aria-label="Next photo" onClick={() => setLbIndex((i) => (i + 1) % launch.length)}>
          <Arrow />
        </button>
      </dialog>

      {/* Verify */}
      {show('verify') && (
        <section className="sec" id="verify">
          <div className="wrap">
            <div className="vf rv">
              <div>
                <span className="bk">{t('homeV2.verify.kicker')}</span>
                <h2>
                  {t('homeV2.verify.heading')}
                  <br />
                  <em>{t('homeV2.verify.headingEm')}</em>
                </h2>
                <p>{t('homeV2.verify.body')}</p>
                <ol className="vsteps">
                  {[1, 2, 3].map((n) => (
                    <li key={n}>
                      <span>
                        {t(`homeV2.verify.step${n}`)}
                        <small>{t(`homeV2.verify.step${n}Note`)}</small>
                      </span>
                    </li>
                  ))}
                </ol>
                <Link className="pbtn" to={prefix + '/verify'}>
                  <span>{t('homeV2.verify.cta')}</span>
                  <i>
                    <Arrow />
                  </i>
                </Link>
                <p className="note2">{t('homeV2.verify.note')}</p>
              </div>
              <div className="vart2">
                <div className="vclip rv">
                  <InViewVideo src={A + 'vid-verify.mp4'} poster={A + 'vid-verify.jpg'} />
                  <button
                    type="button"
                    className="vfull"
                    onClick={() => {
                      guide.current?.showModal();
                      const v = guideVideo.current;
                      if (v) {
                        v.currentTime = 0;
                        v.play().catch(() => {});
                      }
                    }}
                  >
                    <Play />
                    <span>{t('homeV2.verify.guide')}</span>
                  </button>
                </div>
                <figure className="holo rv" style={{ ['--d' as string]: '.2s' }}>
                  <span className="hcard" ref={hcard} onPointerMove={tilt} onPointerLeave={untilt}>
                    <img src={holoImg} alt="The Rx.K Seoul silver hologram sticker with QR code and serial number" />
                    <i aria-hidden="true" />
                  </span>
                  <figcaption>
                    <b>{t('homeV2.verify.holoTitle')}</b>
                    {t('homeV2.verify.holoNote')}
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>
      )}
      <dialog
        className="vlb"
        ref={guide}
        aria-label="Verify guide"
        onClick={(e) => e.target === guide.current && closeDialog(guide, guideVideo)}
        onClose={() => guideVideo.current?.pause()}
      >
        <video ref={guideVideo} src={A + 'vid-verify-full.mp4'} poster={A + 'vid-verify.jpg'} controls playsInline preload="none" style={{ aspectRatio: '4/5' }} />
        <button type="button" className="vx" aria-label="Close video" onClick={() => closeDialog(guide, guideVideo)}>
          ×
        </button>
      </dialog>

      {/* Services */}
      {show('services') && (
        <div className="svc">
          <div className="wrap">
            {(['cod', 'van', 'shield', 'flask'] as const).map((ic, i) => (
              <div key={ic}>
                <span className="ic2">{ICON[ic]}</span>
                <span>
                  <b>{t(`homeV2.services.s${i + 1}Title`)}</b>
                  {t(`homeV2.services.s${i + 1}Body`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {routineSet && items[0]?.key === 'set' && (
        <div className={`sbar${sbar ? ' on' : ''}`}>
          <div className="wrap">
            <img src={routineSet.image} alt="" />
            <div className="t">
              <b>{items[0].name}</b>
              <small>
                {t('homeV2.sbar.giftBox')}
                {routineSet.savings ? ` · ${t('homeV2.sbar.save')} ${routineSet.savings}` : ''}
              </small>
            </div>
            <b style={{ font: '500 18px var(--serif)' }}>{routineSet.price}</b>
            <button type="button" className="btn wine" onClick={() => add(items[0])}>
              {t('homeV2.sbar.add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
